import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackCallConversion, trackLeadConversion } from '../utils/tracking';
import {
  Laptop,
  ShieldCheck,
  Award,
  Clock,
  Wrench,
  Battery,
  Zap,
  Cpu,
  Droplets,
  Volume2,
  Sparkles,
  CheckCircle2,
  PhoneCall,
  ArrowRight,
  ChevronDown,
  Star,
  X,
  CheckCircle,
  HelpCircle,
  Monitor,
  KeyRound,
  Sliders,
  Flame,
  UserCheck,
  BadgePercent,
  Truck,
  Layers,
  ChevronRight,
  Phone
} from 'lucide-react';
import './MacbookRepair.css';

// --- DATA: 8 MacBook Issue Diagnostics ---
const MACBOOK_ISSUES = [
  {
    id: 'screen-repair',
    icon: Monitor,
    title: 'MacBook Screen Repair',
    desc: 'Cracked Retina display, horizontal lines, backlight stage lighting, or black screen? We replace damaged panels with OEM-grade Liquid Retina assemblies.',
    serviceName: 'MacBook Screen Replacement'
  },
  {
    id: 'battery-replacement',
    icon: Battery,
    title: 'Battery Replacement',
    desc: '"Service Recommended" warning, swollen battery pushing trackpad, or quick battery drainage restored with certified high-cycle battery cells.',
    serviceName: 'MacBook Battery Replacement'
  },
  {
    id: 'keyboard-repair',
    icon: KeyRound,
    title: 'Keyboard Repair',
    desc: 'Sticky, unresponsive, or repeating keys on Magic Keyboards and Butterfly mechanism MacBooks. Individual key switch or full top-case replacement.',
    serviceName: 'MacBook Keyboard Repair'
  },
  {
    id: 'charging-issue',
    icon: Zap,
    title: 'Charging Issue Repair',
    desc: 'MagSafe 3 / USB-C Thunderbolt ports not drawing power, loose connections, or intermittent charging fixed with precision port replacement.',
    serviceName: 'MacBook Charging Port Repair'
  },
  {
    id: 'motherboard-repair',
    icon: Cpu,
    title: 'Motherboard Repair',
    desc: 'Advanced logic board microsoldering, short circuit diagnostics, NAND power IC, PMIC repair, and dead MacBook power recovery without full board swaps.',
    serviceName: 'MacBook Motherboard Repair'
  },
  {
    id: 'liquid-damage',
    icon: Droplets,
    title: 'Liquid Damage Repair',
    desc: 'Coffee, water, or beverage spill recovery. Ultrasonic logic board chemical wash, corrosion trace repair, and emergency data preservation.',
    serviceName: 'MacBook Liquid Damage Repair'
  },
  {
    id: 'trackpad-repair',
    icon: Sliders,
    title: 'Trackpad Repair',
    desc: 'Force Touch haptic feedback failure, erratic mouse cursor movements, or cracked glass trackpad module replaced with genuine calibration.',
    serviceName: 'MacBook Trackpad Repair'
  },
  {
    id: 'heating-performance',
    icon: Flame,
    title: 'Performance & Heating Issues',
    desc: 'Loud fan noise, severe thermal throttling, slow performance, or kernel panics resolved with deep thermal paste repasting and fan cleaning.',
    serviceName: 'MacBook Performance & Heating Fix'
  }
];

// --- DATA: Why Choose DSMR Feature Items ---
const WHY_CHOOSE_FEATURES = [
  {
    icon: Wrench,
    title: 'Precision Diagnosis',
    desc: 'We identify the actual issue and root cause before recommending any repair, ensuring zero unnecessary part replacements.'
  },
  {
    icon: UserCheck,
    title: 'Skilled Technicians',
    desc: 'Apple-hardware specialists trained in delicate ribbon cables, proprietary pentalobe screws, and logic board microsoldering.'
  },
  {
    icon: BadgePercent,
    title: 'Transparent Pricing',
    desc: 'Understand exactly what you are paying for with upfront fixed estimates and zero surprise fees before the repair begins.'
  },
  {
    icon: Award,
    title: 'Quality Parts',
    desc: 'Reliable replacement components selected for OEM performance, color accuracy, battery longevity, and long-term durability.'
  },
  {
    icon: Truck,
    title: 'Convenient Service',
    desc: 'Professional doorstep MacBook repair at your home or office across Delhi NCR with express same-day turnaround.'
  }
];

// --- DATA: MacBook Models ---
const MACBOOK_MODELS = [
  {
    title: 'MacBook Air',
    image: '/images/macbook/macbook_air.jpg',
    alt: 'Apple MacBook Air M1 M2 M3 repair services',
    variants: ['MacBook Air M1 (2020)', 'MacBook Air M2 (13" & 15")', 'MacBook Air M3 (13" & 15")', 'Intel MacBook Air (2015-2020)'],
    recommendedService: 'MacBook Air Repair'
  },
  {
    title: 'MacBook Pro',
    image: '/images/macbook/macbook_pro.jpg',
    alt: 'Apple MacBook Pro Liquid Retina repair services',
    variants: ['MacBook Pro 13" (M1 / M2 / Intel)', 'MacBook Pro 14" (M1 / M2 / M3 / M4 Pro/Max)', 'MacBook Pro 16" (Apple Silicon)', 'Intel MacBook Pro (Touch Bar & Non-Touch Bar)', 'Apple Silicon M-Series MacBooks'],
    recommendedService: 'MacBook Pro Repair'
  },
  {
    title: 'Older MacBooks',
    image: '/images/macbook/macbook_older.jpg',
    alt: 'Classic Unibody and 12-inch Retina MacBook repair',
    variants: ['12-inch Retina MacBook', 'Older MacBook Pro Unibody (MagSafe 2)', 'Older MacBook Air (11" & 13")', 'Vintage Aluminum MacBooks'],
    recommendedService: 'Older MacBook Repair'
  }
];

// --- DATA: 5-Step Repair Process ---
const REPAIR_STEPS = [
  {
    step: '01',
    title: 'Tell Us the Problem',
    desc: 'Select your MacBook model and describe the issue via our online booking form or a quick phone call.'
  },
  {
    step: '02',
    title: 'Get Expert Diagnosis',
    desc: 'Our certified technician assesses the hardware/software symptoms and confirms the precise repair required.'
  },
  {
    step: '03',
    title: 'Approve the Repair',
    desc: 'You receive clear, upfront fixed pricing with all part options before any repair work commences.'
  },
  {
    step: '04',
    title: 'Precision Repair',
    desc: 'Your MacBook is repaired using professional ESD-safe tools, microsoldering stations, and OEM-grade components.'
  },
  {
    step: '05',
    title: 'Device Ready',
    desc: 'Comprehensive 20-point quality checks are completed before your restored MacBook is handed back with warranty.'
  }
];

// --- DATA: Popular MacBook Repairs with Dynamic Starting Prices ---
const POPULAR_REPAIRS = [
  {
    icon: Monitor,
    name: 'MacBook Screen Replacement',
    price: '4,999',
    popular: true
  },
  {
    icon: Battery,
    name: 'MacBook Battery Replacement',
    price: '2,499',
    popular: true
  },
  {
    icon: KeyRound,
    name: 'MacBook Keyboard Repair',
    price: '2,199',
    popular: false
  },
  {
    icon: Zap,
    name: 'MacBook Charging Port Repair',
    price: '1,799',
    popular: false
  },
  {
    icon: Cpu,
    name: 'MacBook Motherboard Repair',
    price: '3,999',
    popular: true
  },
  {
    icon: Droplets,
    name: 'MacBook Liquid Damage Repair',
    price: '2,999',
    popular: false
  }
];

// --- DATA: Customer Reviews ---
const CUSTOMER_REVIEWS = [
  {
    name: 'Vikramaditya Roy',
    repair: 'MacBook Pro 16" (M1 Max) — Liquid Damage Recovery',
    rating: 5,
    comment: 'Spilled water right over my M1 Max MacBook Pro. Apple authorized service asked for an astronomical logic board replacement fee. DSMR technician did an ultrasonic board cleaning & component microsoldering. Got it back working in 24 hours with all my video projects intact!'
  },
  {
    name: 'Shreya Sengupta',
    repair: 'MacBook Air M2 — Screen & Display Replacement',
    rating: 5,
    comment: 'Cracked the Liquid Retina screen on my MacBook Air M2 during travel. DSMR arranged a doorstep technician the same afternoon. The display replacement was seamless, True Tone retained, and looks pristine like day one. Highly recommended!'
  },
  {
    name: 'Rohan Mehra',
    repair: 'MacBook Pro 13" — Battery & Thermal Service',
    rating: 5,
    comment: 'My MacBook battery was showing "Service Recommended" and overheating constantly. The technician replaced the battery at my office in Gurgaon in under 45 minutes. Super transparent pricing and zero hidden fees.'
  }
];

// --- DATA: FAQ Accordion Items ---
const FAQ_ITEMS = [
  {
    q: 'How much does MacBook repair cost?',
    a: 'MacBook repair costs depend on your specific model and the affected component. For example, battery replacement starts from ₹ 2,499, charging port repair from ₹ 1,799, and screen replacements from ₹ 4,999. We always provide a transparent upfront quote before starting any repair.'
  },
  {
    q: 'How long does a MacBook repair take?',
    a: 'Most common repairs such as battery replacement, screen swap, SSD upgrades, and keyboard repairs are completed within 45 to 90 minutes right at your doorstep. Board-level microsoldering and severe liquid damage treatments typically take 24 to 48 hours in our ESD-safe cleanroom lab.'
  },
  {
    q: 'Can you repair MacBook motherboard issues?',
    a: 'Yes, absolutely. Our technicians specialize in chip-level logic board repairs including PMIC power IC replacement, short-circuit troubleshooting, capacitor reballing, and SMC/T2/M-series power management restoration, saving you up to 70% compared to full motherboard replacement costs.'
  },
  {
    q: 'Can you repair a liquid-damaged MacBook?',
    a: 'Yes. If liquid is spilled on your MacBook, immediately power it off, do not plug in the charger, and schedule an inspection. We perform ultrasonic chemical cleaning to remove corrosion, fix oxidized solder joints, and rescue your data with an 85%+ success rate.'
  },
  {
    q: 'Do you provide doorstep MacBook repair?',
    a: 'Yes! DSMR Solutions provides convenient doorstep service across Delhi, Gurgaon, Noida, Greater Noida, and Faridabad. Our certified technician comes directly to your home or office with the required diagnostic gear and replacement parts.'
  },
  {
    q: 'Which MacBook models do you repair?',
    a: 'We repair all generations of Apple laptops: MacBook Air (M1, M2, M3, and Intel models), MacBook Pro (13", 14", 16" Apple Silicon and Intel Touch Bar models), 12-inch Retina MacBooks, and vintage aluminum Unibody MacBooks.'
  },
  {
    q: 'Will I know the repair price before work begins?',
    a: 'Yes, 100%. Transparency is our core principle. Our technician will thoroughly inspect your MacBook, explain the exact issue, and present a fixed quote. We only proceed with the repair once you give your explicit approval.'
  }
];

export default function MacbookRepair() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('General MacBook Repair');
  const [selectedModel, setSelectedModel] = useState('MacBook Air M2');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    date: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // SEO: Page title and Schema structured data
  useEffect(() => {
    document.title = 'MacBook Repair Service | Expert MacBook Repairs | DSMR Solutions';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Expert doorstep MacBook repair for MacBook Air, MacBook Pro & Apple Silicon MacBooks across Delhi NCR. Screen, battery, motherboard & liquid damage repairs with transparent pricing.'
      );
    }

    // Insert JSON-LD Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': 'https://dsmrsolutions.in/macbook-repair#service',
          name: 'MacBook Repair Service',
          provider: {
            '@type': 'LocalBusiness',
            name: 'DSMR Solutions',
            telephone: '+918700319769',
            url: 'https://dsmrsolutions.in',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Delhi NCR',
              addressCountry: 'IN'
            }
          },
          serviceType: 'MacBook Laptop Repair',
          description: 'Specialist MacBook repair for MacBook Pro, MacBook Air and Intel MacBooks at your doorstep with genuine quality parts and 90-day warranty.',
          areaServed: ['Delhi', 'Gurgaon', 'Noida', 'Faridabad', 'Ghaziabad', 'Delhi NCR']
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ_ITEMS.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a
            }
          }))
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleOpenBooking = (serviceName = 'General MacBook Repair', modelName = '') => {
    setSelectedService(serviceName);
    if (modelName) setSelectedModel(modelName);
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      mobileNumber: formData.phone || '',
      customerName: formData.name || 'Valued Customer',
      email: formData.email ? formData.email.trim() : null,
      address: formData.address || 'Not provided',
      deviceType: 'MacBook',
      brandModel: `Apple ${selectedModel || 'MacBook'}`.trim(),
      issueDescription: `Service: ${selectedService}. Notes: ${formData.notes || 'None'}. Preferred Date: ${formData.date || 'Earliest available'}`
    };

    try {
      await fetch('https://dsmr-api.vercel.app/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (err) {
      console.warn('Booking API dispatch notice:', err);
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
      trackLeadConversion({
        mobileNumber: formData.phone,
        email: formData.email,
        device: 'MacBook',
      });
      setTimeout(() => {
        setIsModalOpen(false);
        setIsSubmitted(false);
      }, 4500);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <div className="macbook-page">
      {/* ===================================================================
          2. HERO SECTION
          =================================================================== */}
      <section className="macbook-hero">
        <div className="macbook-hero-glow" aria-hidden="true" />
        <div className="macbook-container">
          <div className="macbook-hero-grid">
            {/* Left Content Column */}
            <motion.div
              className="macbook-hero-left"
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <div className="macbook-tag">
                <Sparkles size={14} />
                <span>SPECIALIST MACBOOK REPAIR</span>
              </div>

              <h1 className="macbook-hero-headline">
                MacBook Repair, <br />
                <span className="macbook-gold-text">Done With Precision.</span>
              </h1>

              <p className="macbook-hero-sub">
                Expert repair for MacBook Air, MacBook Pro and other Apple laptops. From display and
                battery replacement to motherboard-level repairs, get professional service with
                transparent pricing and convenient service.
              </p>

              <div className="macbook-hero-ctas">
                <button
                  className="macbook-btn-primary"
                  onClick={() => handleOpenBooking('MacBook Doorstep Repair')}
                  id="hero-book-repair-btn"
                >
                  <Laptop size={18} />
                  <span>Book MacBook Repair</span>
                </button>

                <a href="#whats-wrong" className="macbook-btn-secondary" id="hero-free-diagnosis-btn">
                  <span>Get Free Diagnosis</span>
                  <ArrowRight size={16} />
                </a>

                <a
                  href="tel:+918700319769"
                  className="macbook-btn-call"
                  aria-label="Call DSMR Solutions"
                  data-dsmr-phone="true"
                  onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
                >
                  <Phone size={16} />
                  <span>Call +91 8700319769</span>
                </a>
              </div>

              {/* Compact Trust Indicators */}
              <div className="macbook-hero-trust">
                <div className="hero-trust-item">
                  <UserCheck size={16} className="hero-trust-icon" />
                  <span>Expert Technicians</span>
                </div>
                <div className="hero-trust-item">
                  <Award size={16} className="hero-trust-icon" />
                  <span>Quality Parts</span>
                </div>
                <div className="hero-trust-item">
                  <BadgePercent size={16} className="hero-trust-icon" />
                  <span>Transparent Pricing</span>
                </div>
                <div className="hero-trust-item">
                  <Truck size={16} className="hero-trust-icon" />
                  <span>Convenient Service</span>
                </div>
              </div>
            </motion.div>

            {/* Right MacBook Floating Visual */}
            <motion.div
              className="macbook-hero-right"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            >
              <div className="macbook-hero-visual-wrap">
                <img
                  src="/images/macbook/macbook_hero.jpg"
                  alt="Precision MacBook Repair Service by DSMR Solutions"
                  className="macbook-hero-image"
                  width={640}
                  height={480}
                  loading="eager"
                />

                {/* Floating Badge Top Left */}
                <div className="hero-floating-badge badge-top-left">
                  <div className="badge-icon-box">
                    <Cpu size={18} />
                  </div>
                  <div>
                    <span className="badge-title">Apple Silicon & Intel</span>
                    <span className="badge-subtitle">M1, M2, M3, M4 & Intel Series</span>
                  </div>
                </div>

                {/* Floating Badge Bottom Right */}
                <div className="hero-floating-badge badge-bottom-right">
                  <div className="badge-icon-box">
                    <ShieldCheck size={18} />
                  </div>
                  <div>
                    <span className="badge-title">90-Day Warranty</span>
                    <span className="badge-subtitle">On Every Component & Labor</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          3. TRUST / STATISTICS STRIP (Navy Background)
          =================================================================== */}
      <section className="macbook-stats-section" id="stats">
        <div className="macbook-container">
          <div className="macbook-stats-header">
            <h2 className="macbook-stats-headline">
              PREMIUM MACBOOK REPAIR. WITHOUT THE PREMIUM DRAMA.
            </h2>
          </div>

          <div className="macbook-stats-grid">
            {/* Stat 1: Devices Repaired */}
            <div className="macbook-stat-card">
              <div className="stat-icon-wrapper">
                <Laptop size={24} />
              </div>
              <div className="stat-val-text">12,000+</div>
              <div className="stat-label-text">Devices Repaired</div>
            </div>

            {/* Stat 2: Years Experience */}
            <div className="macbook-stat-card">
              <div className="stat-icon-wrapper">
                <Award size={24} />
              </div>
              <div className="stat-val-text">8+</div>
              <div className="stat-label-text">Years Experience</div>
            </div>

            {/* Stat 3: Quick Response */}
            <div className="macbook-stat-card">
              <div className="stat-icon-wrapper">
                <Clock size={24} />
              </div>
              <div className="stat-val-text">30 min</div>
              <div className="stat-label-text">Quick Response Time</div>
            </div>

            {/* Stat 4: Customer Rating */}
            <div className="macbook-stat-card">
              <div className="stat-icon-wrapper">
                <Star size={24} fill="#F5B93A" color="#F5B93A" />
              </div>
              <div className="stat-val-text">4.9★</div>
              <div className="stat-label-text">Customer Rating (Google)</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          4. WHAT'S WRONG WITH YOUR MACBOOK? (Cream/Light Background)
          =================================================================== */}
      <section className="macbook-issues-section" id="whats-wrong">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag tag-light">
              <Wrench size={14} />
              <span>PRECISION DIAGNOSTICS</span>
            </div>
            <h2 className="macbook-section-title">What's Wrong With Your MacBook?</h2>
            <p className="macbook-section-desc">
              Select your symptom below for an instant diagnostic breakdown, turnaround estimate, and
              doorstep repair booking.
            </p>
          </div>

          <div className="macbook-issues-grid">
            {MACBOOK_ISSUES.map((issue) => {
              const IconComponent = issue.icon;
              return (
                <div
                  key={issue.id}
                  className="macbook-issue-card"
                  onClick={() => handleOpenBooking(issue.serviceName)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleOpenBooking(issue.serviceName)}
                >
                  <div className="issue-icon-box">
                    <IconComponent size={24} />
                  </div>
                  <h3 className="issue-card-title">{issue.title}</h3>
                  <p className="issue-card-desc">{issue.desc}</p>
                  <div className="issue-card-action">
                    <span>Book Diagnostic</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================================================================
          5. WHY CHOOSE DSMR SOLUTIONS? (Split 2-Column Section)
          =================================================================== */}
      <section className="macbook-why-section" id="why-choose-us">
        <div className="macbook-container">
          <div className="macbook-why-grid">
            {/* Left: Generated Technician Repair Visual */}
            <div className="macbook-why-image-wrap">
              <img
                src="/images/macbook/macbook_technician.jpg"
                alt="Expert technician repairing Apple MacBook internals on ESD-safe bench"
                className="macbook-why-image"
                width={600}
                height={500}
                loading="lazy"
              />
              <div className="why-image-overlay-badge">
                <div className="why-badge-icon">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <span className="why-badge-title">ESD-Safe Cleanroom Protocol</span>
                  <span className="why-badge-sub">Microsoldering & Microscopic Diagnostics</span>
                </div>
              </div>
            </div>

            {/* Right: Premium Value Propositions */}
            <div className="macbook-why-content">
              <div className="macbook-tag">
                <Award size={14} />
                <span>THE DSMR DIFFERENCE</span>
              </div>
              <h2>
                Your MacBook Deserves Better <br />
                <span className="macbook-gold-text">Than a Random Repair Shop.</span>
              </h2>

              <div className="macbook-features-list">
                {WHY_CHOOSE_FEATURES.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div className="macbook-feature-item" key={idx}>
                      <div className="feature-icon-box">
                        <Icon size={22} />
                      </div>
                      <div>
                        <h3 className="feature-item-title">{feature.title}</h3>
                        <p className="feature-item-desc">{feature.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          6. MACBOOK MODELS WE REPAIR (3 Premium Cards)
          =================================================================== */}
      <section className="macbook-models-section" id="models">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag">
              <Layers size={14} />
              <span>FULL MODEL COMPATIBILITY</span>
            </div>
            <h2 className="macbook-section-title">We Repair Every Major MacBook Model</h2>
            <p className="macbook-section-desc">
              We stock certified replacement parts and specialized micro-tools for every Apple laptop
              generation.
            </p>
          </div>

          <div className="macbook-models-grid">
            {MACBOOK_MODELS.map((model, idx) => (
              <div className="macbook-model-card" key={idx}>
                <div className="model-img-wrapper">
                  <img
                    src={model.image}
                    alt={model.alt}
                    className="model-card-img"
                    width={400}
                    height={220}
                    loading="lazy"
                  />
                </div>
                <div className="model-card-content">
                  <h3 className="model-card-title">{model.title}</h3>
                  <div className="model-chips-list">
                    {model.variants.map((variant, vIdx) => (
                      <span key={vIdx} className="model-chip-item">
                        {variant}
                      </span>
                    ))}
                  </div>
                  <button
                    className="model-card-cta"
                    onClick={() => handleOpenBooking(model.recommendedService, model.title)}
                  >
                    Select {model.title}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          7. REPAIR PROCESS (Full-Width Navy 5-Step Timeline)
          =================================================================== */}
      <section className="macbook-process-section" id="process">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag">
              <Sliders size={14} />
              <span>SEAMLESS WORKFLOW</span>
            </div>
            <h2 className="macbook-section-title">Our Repair Process</h2>
            <p className="macbook-section-desc">
              Transparent, professional, and completely hassle-free from booking to warranty handover.
            </p>
          </div>

          <div className="macbook-process-timeline">
            {REPAIR_STEPS.map((step, idx) => (
              <div className="process-step-card" key={idx}>
                <div className="step-num-badge">{step.step}</div>
                <h3 className="step-card-title">{step.title}</h3>
                <p className="step-card-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          8. POPULAR MACBOOK REPAIRS (Pricing Cards)
          =================================================================== */}
      <section className="macbook-pricing-section" id="pricing">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag">
              <BadgePercent size={14} />
              <span>TRANSPARENT PRICING</span>
            </div>
            <h2 className="macbook-section-title">Popular MacBook Repairs</h2>
            <p className="macbook-section-desc">
              Clear upfront estimates with zero hidden labor costs. Select a repair to get your exact quote.
            </p>
          </div>

          <div className="macbook-pricing-grid">
            {POPULAR_REPAIRS.map((repair, idx) => {
              const Icon = repair.icon;
              return (
                <div className="pricing-repair-card" key={idx}>
                  <div className="pricing-icon-row">
                    <div className="pricing-icon-box">
                      <Icon size={24} />
                    </div>
                    {repair.popular && (
                      <span className="pricing-badge-popular">Most Requested</span>
                    )}
                  </div>

                  <h3 className="pricing-card-title">{repair.name}</h3>

                  <div className="pricing-amount-wrap">
                    <span className="price-sub-label">Starting from</span>
                    <div className="price-main-val">₹ {repair.price}</div>
                  </div>

                  <button
                    className="pricing-quote-btn"
                    onClick={() => handleOpenBooking(repair.name)}
                  >
                    <span>Get Exact Quote</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              );
            })}
          </div>

          <p className="pricing-disclaimer-note">
            * Final pricing depends on your MacBook model, year of manufacture, and the extent of damage.
          </p>
        </div>
      </section>

      {/* ===================================================================
          9. CUSTOMER REVIEWS (Navy Background)
          =================================================================== */}
      <section className="macbook-reviews-section" id="reviews">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag">
              <Star size={14} fill="#F5B93A" color="#F5B93A" />
              <span>VERIFIED EXPERIENCES</span>
            </div>
            <h2 className="macbook-section-title">Trusted With Devices That Matter</h2>
            <p className="macbook-section-desc">
              Read how our certified technicians restored high-end MacBooks for creative professionals and business leaders.
            </p>
          </div>

          <div className="macbook-reviews-grid">
            {CUSTOMER_REVIEWS.map((review, idx) => (
              <div className="macbook-review-card" key={idx}>
                <div>
                  <div className="review-stars-row">
                    {[...Array(review.rating)].map((_, sIdx) => (
                      <Star key={sIdx} size={16} fill="#F5B93A" color="#F5B93A" />
                    ))}
                  </div>
                  <p className="review-quote-text">"{review.comment}"</p>
                </div>

                <div className="review-user-info">
                  <div className="review-avatar-circle">{review.name.charAt(0)}</div>
                  <div>
                    <h4 className="review-user-name">{review.name}</h4>
                    <span className="review-repair-type">{review.repair}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          10. FAQ SECTION (Cream Background Accordion)
          =================================================================== */}
      <section className="macbook-faq-section" id="faq">
        <div className="macbook-container">
          <div className="macbook-section-header center">
            <div className="macbook-tag tag-light">
              <HelpCircle size={14} />
              <span>CLARITY & TRANSPARENCY</span>
            </div>
            <h2 className="macbook-section-title">Frequently Asked Questions</h2>
            <p className="macbook-section-desc">
              Everything you need to know about our MacBook doorstep repair service, pricing, and warranty.
            </p>
          </div>

          <div className="macbook-faq-list">
            {FAQ_ITEMS.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div className={`macbook-faq-item ${isOpen ? 'active' : ''}`} key={idx}>
                  <button
                    className="faq-trigger-btn"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                  >
                    <span className="faq-question-text">{faq.q}</span>
                    <ChevronDown size={20} className="faq-icon-chevron" />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="faq-answer-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================================================================
          11. FINAL CTA SECTION (Dramatic Navy Section)
          =================================================================== */}
      <section className="macbook-final-cta-section" id="final-cta">
        <div className="macbook-container">
          <div className="final-cta-card">
            {/* Visual on the Left */}
            <div className="final-cta-image-wrap">
              <img
                src="/images/macbook/macbook_pro.jpg"
                alt="MacBook Pro ready for precision doorstep repair"
                className="final-cta-img"
                width={480}
                height={320}
                loading="lazy"
              />
            </div>

            {/* Content on the Right */}
            <div className="final-cta-content">
              <div className="final-cta-tag">YOUR MACBOOK. BACK TO WORK.</div>
              <h2 className="final-cta-headline">
                Don't Let a Faulty MacBook <br />
                <span className="macbook-gold-text">Slow You Down.</span>
              </h2>
              <p className="final-cta-desc">
                Get your MacBook diagnosed and repaired by experienced professionals with doorstep convenience and a guaranteed 90-day warranty.
              </p>

              <div className="final-cta-buttons">
                <button
                  className="macbook-btn-primary"
                  onClick={() => handleOpenBooking('Priority MacBook Repair')}
                >
                  <Laptop size={18} />
                  <span>Book MacBook Repair</span>
                </button>

                <button
                  className="macbook-btn-secondary"
                  onClick={() => handleOpenBooking('Free MacBook Repair Quote')}
                >
                  <span>Get a Free Repair Quote</span>
                  <ArrowRight size={16} />
                </button>
              </div>

              <div className="final-cta-trust">
                <div className="final-trust-pill">
                  <CheckCircle2 size={16} />
                  <span>Professional Care</span>
                </div>
                <div className="final-trust-pill">
                  <ShieldCheck size={16} />
                  <span>Quality Parts</span>
                </div>
                <div className="final-trust-pill">
                  <BadgePercent size={16} />
                  <span>Transparent Pricing</span>
                </div>
                <div className="final-trust-pill">
                  <Truck size={16} />
                  <span>Hassle-Free Service</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          12. INTERACTIVE BOOKING / QUOTE MODAL
          =================================================================== */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="macbook-modal-backdrop" onClick={() => setIsModalOpen(false)}>
            <motion.div
              className="macbook-modal-card"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <button
                className="macbook-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close dialog"
              >
                <X size={18} />
              </button>

              {!isSubmitted ? (
                <>
                  <div className="modal-header-block">
                    <div className="modal-header-icon">
                      <Laptop size={24} />
                    </div>
                    <h3 className="modal-header-title">Book MacBook Repair</h3>
                    <p className="modal-header-sub">
                      Selected Service: <strong style={{ color: '#F5B93A' }}>{selectedService}</strong>
                    </p>
                  </div>

                  <form className="macbook-modal-form" onSubmit={handleFormSubmit}>
                    <div className="modal-form-row">
                      <div className="modal-form-group">
                        <label>Your Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Rahul Sharma"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </div>

                      <div className="modal-form-group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          required
                          placeholder="e.g. 9876543210"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="modal-form-row">
                      <div className="modal-form-group">
                        <label>MacBook Model *</label>
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                        >
                          <option value="MacBook Air M1 (2020)">MacBook Air M1 (2020)</option>
                          <option value="MacBook Air M2 (2022/2023)">MacBook Air M2 (2022/2023)</option>
                          <option value="MacBook Air M3 (2024)">MacBook Air M3 (2024)</option>
                          <option value="MacBook Pro 13-inch (M1/M2/Intel)">MacBook Pro 13" (M1/M2/Intel)</option>
                          <option value="MacBook Pro 14-inch (M1/M2/M3 Pro/Max)">MacBook Pro 14" (Apple Silicon)</option>
                          <option value="MacBook Pro 16-inch (M1/M2/M3 Pro/Max)">MacBook Pro 16" (Apple Silicon)</option>
                          <option value="Intel MacBook Pro (Retina / Touch Bar)">Intel MacBook Pro (Touch Bar / Retina)</option>
                          <option value="Intel MacBook Air (2015-2020)">Intel MacBook Air</option>
                          <option value="12-inch Retina MacBook">12-inch Retina MacBook</option>
                          <option value="Other Apple Laptop">Other Apple Laptop</option>
                        </select>
                      </div>

                      <div className="modal-form-group">
                        <label>Primary Issue *</label>
                        <select
                          value={selectedService}
                          onChange={(e) => setSelectedService(e.target.value)}
                        >
                          <option value="MacBook Screen Replacement">Screen / Display Issue</option>
                          <option value="MacBook Battery Replacement">Battery Drain / Service Warning</option>
                          <option value="MacBook Keyboard Repair">Keyboard / Sticky Keys</option>
                          <option value="MacBook Charging Port Repair">Charging / MagSafe / USB-C Port</option>
                          <option value="MacBook Motherboard Repair">Motherboard / No Power / Dead</option>
                          <option value="MacBook Liquid Damage Repair">Water / Liquid Spill Damage</option>
                          <option value="MacBook Trackpad Repair">Trackpad / Click Unresponsive</option>
                          <option value="MacBook Performance & Heating Fix">Overheating / Fan Noise / Slow</option>
                          <option value="General MacBook Diagnostic">Free General Diagnostic</option>
                        </select>
                      </div>
                    </div>

                    <div className="modal-form-group">
                      <label>Doorstep Service Address *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Flat 402, Tower B, Cyber City, Gurgaon"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </div>

                    <div className="modal-form-row">
                      <div className="modal-form-group">
                        <label>Preferred Date</label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        />
                      </div>

                      <div className="modal-form-group">
                        <label>Email (Optional)</label>
                        <input
                          type="email"
                          placeholder="e.g. rahul@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="modal-form-group">
                      <label>Additional Notes / Symptoms</label>
                      <textarea
                        rows="2"
                        placeholder="Any additional details (e.g. liquid spill happened 2 hours ago, error codes, etc.)"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      />
                    </div>

                    <button
                      type="submit"
                      className="modal-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span>Submitting Request...</span>
                      ) : (
                        <>
                          <CheckCircle size={18} />
                          <span>Confirm Booking</span>
                        </>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="modal-success-state">
                  <div className="modal-success-icon">
                    <CheckCircle2 size={42} />
                  </div>
                  <h3 className="modal-success-title">MacBook Repair Booked!</h3>
                  <p className="modal-success-desc">
                    Thank you, <strong>{formData.name || 'valued customer'}</strong>. Your request for{' '}
                    <strong>{selectedModel} ({selectedService})</strong> has been received. Our Apple-certified
                    technician is being assigned and will call you at{' '}
                    <strong>{formData.phone}</strong> shortly.
                  </p>
                  <div className="modal-success-dispatch">
                    Doorstep Technician Dispatched
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
